import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Search, Pencil, Trash2, Pill, Loader2, Download, Upload, ClipboardEdit, ChevronLeft, ChevronRight, Camera, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { useMedTypes, isStripType, unitLabelOf, medTypeLabelOf } from "@/lib/medTypes";
import { MedTypeManager } from "@/components/MedTypeManager";

const empty = {
  name: "", generic_name: "", manufacturer: "", category: "General", subcategory: "", med_type: "tablet",
  batch_no: "", hsn_code: "3004", pack_size: 10, strip_mrp: 0, purchase_price: 0,
  gst_percent: 12, stock_strips: 0, stock_loose_tablets: 0, expiry_date: "", rack: "", description: "",
};

function expiryInfo(dateStr) {
  if (!dateStr) return { label: "—", cls: "bg-slate-100 text-slate-500 hover:bg-slate-100" };
  const days = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
  if (days < 0) return { label: "Expired", cls: "bg-red-500 text-white hover:bg-red-500" };
  if (days <= 90) return { label: `${days}d left`, cls: "bg-amber-400 text-amber-950 hover:bg-amber-400" };
  return { label: dateStr, cls: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" };
}

function stockInfo(m) {
  const totalTabs = m.stock_strips * m.pack_size + m.stock_loose_tablets;
  if (totalTabs === 0) return { label: "Out", cls: "bg-red-500 text-white hover:bg-red-500" };
  if (m.stock_strips < 10) return { label: "Low", cls: "bg-amber-400 text-amber-950 hover:bg-amber-400" };
  return { label: "In stock", cls: "bg-emerald-600 text-white hover:bg-emerald-600" };
}

export default function Medicines() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [stock, setStock] = useState("all");
  const [expiry, setExpiry] = useState("all");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(0);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [adjustMed, setAdjustMed] = useState(null);
  const [adjStrips, setAdjStrips] = useState("");
  const [adjLoose, setAdjLoose] = useState("");
  const [adjReason, setAdjReason] = useState("");
  const [imgFor, setImgFor] = useState(null);
  const [imgUploading, setImgUploading] = useState(false);
  const imgFileRef = useRef(null);
  const [catTree, setCatTree] = useState([]);
  const [typesVer, setTypesVer] = useState(0);
  const medTypes = useMedTypes(typesVer);
  const [typeDialog, setTypeDialog] = useState(false);

  useEffect(() => { api.get("/categories").then(({ data }) => setCatTree(data)).catch(() => {}); }, []);

  const load = async () => {
    const { data } = await api.get("/medicines");
    setItems(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => setPage(0), [category, stock, expiry, search, pageSize]);

  const categories = useMemo(
    () => [...new Set(items.map((m) => m.category || "General"))].sort(),
    [items]
  );

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return items.filter((m) => {
      if (category !== "all" && (m.category || "General") !== category) return false;
      const totalTabs = m.stock_strips * m.pack_size + m.stock_loose_tablets;
      if (stock === "out" && totalTabs !== 0) return false;
      if (stock === "low" && !(totalTabs > 0 && m.stock_strips < 10)) return false;
      if (stock === "in" && !(totalTabs > 0 && m.stock_strips >= 10)) return false;
      if (expiry !== "all") {
        const days = m.expiry_date ? Math.ceil((new Date(m.expiry_date) - new Date()) / 86400000) : null;
        if (expiry === "expired" && !(days !== null && days < 0)) return false;
        if (expiry === "expiring" && !(days !== null && days >= 0 && days <= 90)) return false;
        if (expiry === "valid" && !(days === null || days > 90)) return false;
      }
      if (s && ![m.name, m.generic_name, m.manufacturer, m.batch_no, m.category].filter(Boolean).join(" ").toLowerCase().includes(s)) return false;
      return true;
    });
  }, [items, category, stock, expiry, search]);

  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pages - 1);
  const pageItems = filtered.slice(safePage * pageSize, (safePage + 1) * pageSize);

  const onImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/medicines/import", fd);
      toast.success(`Excel import done${data.inserted != null ? ` — ${data.inserted} medicines` : ""}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const onImagePick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !imgFor) return;
    if (!f.name.toLowerCase().endsWith(".webp") && f.type !== "image/webp") {
      return toast.error("Sirf WebP format allowed hai — 600x600 px square, max 500KB.");
    }
    setImgUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/medicines/${imgFor.id}/image`, fd);
      toast.success(`${imgFor.name} ki photo upload ho gayi`);
      setImgFor(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setImgUploading(false);
    }
  };

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (m) => { setEditing(m); setForm({ ...m }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        pack_size: Number(form.pack_size),
        strip_mrp: Number(form.strip_mrp),
        purchase_price: Number(form.purchase_price),
        gst_percent: Number(form.gst_percent),
        stock_strips: Number(form.stock_strips),
        stock_loose_tablets: Number(form.stock_loose_tablets),
      };
      if (editing) await api.put(`/medicines/${editing.id}`, payload);
      else await api.post("/medicines", payload);
      toast.success(editing ? "Medicine updated" : "Medicine added");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const genDescription = async () => {
    if (!editing?.id) {
      toast.error("Pehle medicine Save karein, phir AI description bana sakte hain");
      return;
    }
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/medicine-description", { medicine_id: editing.id });
      setForm((f) => ({ ...f, description: data.description }));
      toast.success("AI description ready — Save karke confirm karein");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "AI se nahi ban paya");
    } finally {
      setAiBusy(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete ${m.name}?`)) return;
    await api.delete(`/medicines/${m.id}`);
    toast.success("Deleted");
    load();
  };

  const submitAdjust = async () => {
    if (!adjReason.trim()) return toast.error("Reason is required");
    try {
      await api.post("/stock/adjust", {
        medicine_id: adjustMed.id,
        physical_strips: Number(adjStrips) || 0,
        physical_loose: Number(adjLoose) || 0,
        reason: adjReason,
      });
      toast.success(`Stock audit: ${adjustMed.name} updated`);
      setAdjustMed(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="medicines-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Medicines Inventory</h1>
          <p className="text-sm text-slate-500">{items.length} SKUs • Filter, paginate, import/export Excel.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid="import-file-input" onChange={onImport} />
          <Button variant="outline" data-testid="import-excel-btn" disabled={importing} onClick={() => fileRef.current?.click()}>
            {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Import Excel
          </Button>
          <Button variant="outline" data-testid="export-inventory-btn" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/export/medicines`, "_blank")}>
            <Download className="w-4 h-4 mr-2" /> Download Excel
          </Button>
          <Button data-testid="add-medicine-btn" onClick={() => navigate("/add-medicine")} className="bg-emerald-900 hover:bg-emerald-800">
            <Plus className="w-4 h-4 mr-2" /> Add Medicine
          </Button>
        </div>
      </div>

      <Card className="border border-slate-300 rounded-lg overflow-hidden">
        <div className="grid grid-cols-2 sm:grid-cols-3 bg-slate-50 border-b border-slate-300">
          <div className="p-3 border-r border-slate-300 max-sm:col-span-2 max-sm:border-b">
            <Label className="text-xs text-slate-600">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="med-filter-category" className="mt-1 h-10 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Category</SelectItem>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="p-3 sm:border-r border-slate-300">
            <Label className="text-xs text-slate-600">Stock Status</Label>
            <Select value={stock} onValueChange={setStock}>
              <SelectTrigger data-testid="med-filter-stock" className="mt-1 h-10 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Stock Status All</SelectItem>
                <SelectItem value="in">In Stock</SelectItem>
                <SelectItem value="low">Low Stock</SelectItem>
                <SelectItem value="out">Out of Stock</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="p-3">
            <Label className="text-xs text-slate-600">Expiry Status</Label>
            <Select value={expiry} onValueChange={setExpiry}>
              <SelectTrigger data-testid="med-filter-expiry" className="mt-1 h-10 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Expiry Status All</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="expiring">Expiring in 90 days</SelectItem>
                <SelectItem value="valid">Valid</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 border-b border-slate-300">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            Show
            <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
              <SelectTrigger data-testid="med-page-size" className="h-8 w-[70px] bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {[10, 25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
            entries
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">Search:</span>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="med-search-input" value={search} onChange={(e) => setSearch(e.target.value)} className="h-8 w-56 pl-8 bg-white" />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table data-testid="inventory-stock-table" className="text-xs [&_th]:border [&_td]:border [&_th]:border-slate-300 [&_td]:border-slate-300 [&_th]:bg-slate-100 [&_th]:text-slate-700 [&_th]:font-bold [&_th]:py-2 [&_td]:py-1.5 [&_th]:px-2 [&_td]:px-2">
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>Photo</TableHead>
                <TableHead>Medicine</TableHead>
                <TableHead>Manufacturer</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Batch</TableHead>
                <TableHead>Expiry</TableHead>
                <TableHead className="text-right">Pack</TableHead>
                <TableHead className="text-right">MRP</TableHead>
                <TableHead className="text-right">Purchase</TableHead>
                <TableHead className="text-right">GST%</TableHead>
                <TableHead className="text-right">Stock</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageItems.map((m, i) => {
                const ex = expiryInfo(m.expiry_date);
                const st = stockInfo(m);
                return (
                  <TableRow key={m.id} data-testid={`med-row-${m.id}`} className="hover:bg-emerald-50/40">
                    <TableCell className="font-mono-num text-slate-500">{safePage * pageSize + i + 1}</TableCell>
                    <TableCell>
                      {m.image_url ? (
                        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/public/medicine-image/${m.id}?v=${m.image_version || ""}`} alt={m.name} className="w-10 h-10 rounded-lg object-cover border border-slate-200" loading="lazy" data-testid={`med-img-${m.id}`} />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center"><Pill className="w-4 h-4 text-emerald-300" /></div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="font-bold text-slate-900">{m.name}</div>
                      <div className="text-[11px] text-slate-500">{m.generic_name || "—"}</div>
                    </TableCell>
                    <TableCell>{m.manufacturer || "—"}</TableCell>
                    <TableCell>
                      <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100 font-medium">{m.category || "General"}</Badge>
                      <div className="text-[10px] text-slate-400 mt-0.5">{[m.subcategory, medTypeLabelOf(m)].filter(Boolean).join(" • ")}</div>
                    </TableCell>
                    <TableCell className="font-mono-num">{m.batch_no || "—"}</TableCell>
                    <TableCell><Badge className={`${ex.cls} font-medium`}>{ex.label}</Badge></TableCell>
                    <TableCell className="text-right font-mono-num">{m.pack_size}</TableCell>
                    <TableCell className="text-right font-mono-num font-semibold">₹{Number(m.strip_mrp).toFixed(2)}</TableCell>
                    <TableCell className="text-right font-mono-num">₹{Number(m.purchase_price).toFixed(2)}</TableCell>
                    <TableCell className="text-right font-mono-num">{m.gst_percent}%</TableCell>
                    <TableCell className="text-right font-mono-num font-semibold">
                      {m.stock_strips}<span className="text-slate-400 font-normal"> +{m.stock_loose_tablets}</span>
                    </TableCell>
                    <TableCell><Badge className={`${st.cls} font-medium`} data-testid={`med-status-${m.id}`}>{st.label}</Badge></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="icon" variant="ghost" title="Upload Photo" className="h-7 w-7" onClick={() => setImgFor(m)} data-testid={`upload-img-${m.id}`}><Camera className="w-3.5 h-3.5 text-emerald-700" /></Button>
                      <Button size="icon" variant="ghost" title="Stock Audit" className="h-7 w-7" onClick={() => { setAdjustMed(m); setAdjStrips(String(m.stock_strips)); setAdjLoose(String(m.stock_loose_tablets)); setAdjReason(""); }} data-testid={`adjust-med-${m.id}`}><ClipboardEdit className="w-3.5 h-3.5 text-sky-700" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(m)} data-testid={`edit-med-${m.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => remove(m)} data-testid={`delete-med-${m.id}`}><Trash2 className="w-3.5 h-3.5 text-red-600" /></Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {pageItems.length === 0 && <TableRow><TableCell colSpan={14} className="text-center py-10 text-slate-400">No medicines found</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 border-t border-slate-300 text-sm text-slate-600">
          <div data-testid="med-table-info">
            Showing {filtered.length === 0 ? 0 : safePage * pageSize + 1} to {Math.min((safePage + 1) * pageSize, filtered.length)} of {filtered.length} entries
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" className="h-8" disabled={safePage === 0} onClick={() => setPage(safePage - 1)} data-testid="med-prev-page"><ChevronLeft className="w-4 h-4" /> Prev</Button>
            <span className="px-2 font-mono-num text-xs">{safePage + 1} / {pages}</span>
            <Button size="sm" variant="outline" className="h-8" disabled={safePage >= pages - 1} onClick={() => setPage(safePage + 1)} data-testid="med-next-page">Next <ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Pill className="w-5 h-5 text-emerald-700" /> {editing ? "Edit" : "Add"} Medicine</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 max-h-[65vh] overflow-y-auto pr-2">
            <Field label="Medicine Name*" v={form.name} on={(v) => setForm({ ...form, name: v })} testid="med-name" />
            <Field label="Generic / Salt" v={form.generic_name} on={(v) => setForm({ ...form, generic_name: v })} />
            <Field label="Manufacturer" v={form.manufacturer} on={(v) => setForm({ ...form, manufacturer: v })} />
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={form.category || "General"} onValueChange={(v) => setForm({ ...form, category: v, subcategory: "" })}>
                <SelectTrigger data-testid="med-form-category" className="mt-1 h-10 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {[...new Set(["General", ...catTree.map((c) => c.name), ...(form.category && form.category !== "General" && !catTree.some((c) => c.name === form.category) ? [form.category] : [])])].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Sub-Category</Label>
              <Select value={form.subcategory || "none"} onValueChange={(v) => setForm({ ...form, subcategory: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="med-form-subcategory" className="mt-1 h-10 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="none">— None —</SelectItem>
                  {(catTree.find((c) => c.name === form.category)?.children || []).map((s) => <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Medicine Type</Label>
              <Select value={form.med_type || "tablet"} onValueChange={(v) => setForm({ ...form, med_type: v, ...(isStripType(v) ? {} : { pack_size: 1, stock_loose_tablets: 0 }) })}>
                <SelectTrigger data-testid="med-form-type" className="mt-1 h-10 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {medTypes.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                  <div className="border-t border-slate-100 mt-1 p-1">
                    <button type="button" data-testid="med-form-type-manage" className="w-full text-left text-xs font-semibold text-emerald-700 rounded px-2 py-1.5 hover:bg-emerald-50" onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }} onClick={() => setTypeDialog(true)}>+ Add / Manage Types</button>
                  </div>
                </SelectContent>
              </Select>
              <button type="button" onClick={() => setTypeDialog(true)} className="text-[11px] text-emerald-700 font-semibold mt-1 hover:underline" data-testid="manage-types-link">+ Add / Manage Types</button>
            </div>
            <Field label="Batch No." v={form.batch_no} on={(v) => setForm({ ...form, batch_no: v })} />
            <Field label="HSN Code" v={form.hsn_code} on={(v) => setForm({ ...form, hsn_code: v })} />
            {isStripType(form.med_type) && <Field label="Pack Size (tabs/strip)" type="number" v={form.pack_size} on={(v) => setForm({ ...form, pack_size: v })} />}
            <Field label={`${unitLabelOf(form)} MRP (₹)`} type="number" v={form.strip_mrp} on={(v) => setForm({ ...form, strip_mrp: v })} />
            <Field label={`Purchase Cost (₹/${unitLabelOf(form).toLowerCase()})`} type="number" v={form.purchase_price} on={(v) => setForm({ ...form, purchase_price: v })} />
            <Field label="GST %" type="number" v={form.gst_percent} on={(v) => setForm({ ...form, gst_percent: v })} />
            <Field label={`Stock (${unitLabelOf(form).toLowerCase()}s)`} type="number" v={form.stock_strips} on={(v) => setForm({ ...form, stock_strips: v })} />
            {isStripType(form.med_type) && <Field label="Loose tablets" type="number" v={form.stock_loose_tablets} on={(v) => setForm({ ...form, stock_loose_tablets: v })} />}
            <Field label="Expiry (YYYY-MM-DD)" v={form.expiry_date} on={(v) => setForm({ ...form, expiry_date: v })} placeholder="2027-06-30" />
            <Field label="Rack" v={form.rack} on={(v) => setForm({ ...form, rack: v })} />
            <div className="col-span-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Website Description (storefront par dikhti hai)</Label>
                <Button type="button" size="sm" variant="outline" onClick={genDescription} disabled={aiBusy} data-testid="ai-desc-generate-btn"
                  className="h-7 text-xs text-violet-700 border-violet-300 hover:bg-violet-50">
                  {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                  AI se likho
                </Button>
              </div>
              <textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3}
                data-testid="med-description-input" placeholder="Medicine save karne ke baad 'AI se likho' dabayen, ya khud likhein…"
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.name} data-testid="save-medicine-btn" className="bg-emerald-900 hover:bg-emerald-800">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MedTypeManager open={typeDialog} onOpenChange={setTypeDialog} types={medTypes} onSaved={() => setTypesVer((v) => v + 1)} />

      <Dialog open={!!adjustMed} onOpenChange={(o) => !o && setAdjustMed(null)}>
        <DialogContent className="max-w-sm bg-white" data-testid="adjust-dialog">
          <DialogHeader><DialogTitle className="font-display">Stock Audit — {adjustMed?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-500">{adjustMed && (isStripType(adjustMed.med_type) ? `System stock: ${adjustMed.stock_strips} strips + ${adjustMed.stock_loose_tablets} loose` : `System stock: ${adjustMed.stock_strips} ${unitLabelOf(adjustMed).toLowerCase()}s`)}</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Physical Strips</Label>
                <Input data-testid="adjust-strips" type="number" min="0" value={adjStrips} onChange={(e) => setAdjStrips(e.target.value)} className="mt-1 h-10 font-mono-num" />
              </div>
              <div>
                <Label className="text-xs">Physical Loose</Label>
                <Input data-testid="adjust-loose" type="number" min="0" value={adjLoose} onChange={(e) => setAdjLoose(e.target.value)} className="mt-1 h-10 font-mono-num" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Reason *</Label>
              <Input data-testid="adjust-reason" value={adjReason} onChange={(e) => setAdjReason(e.target.value)} className="mt-1 h-10" placeholder="Damage / counting mismatch / expiry dispose" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustMed(null)}>Cancel</Button>
            <Button onClick={submitAdjust} data-testid="adjust-submit" className="bg-emerald-900 hover:bg-emerald-800">Save Audit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <input ref={imgFileRef} type="file" accept="image/webp" className="hidden" data-testid="med-image-input" onChange={onImagePick} />

      <Dialog open={!!imgFor} onOpenChange={(o) => !o && setImgFor(null)}>
        <DialogContent className="max-w-sm bg-white" data-testid="med-image-dialog">
          <DialogHeader><DialogTitle className="font-display">Medicine Photo — {imgFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {imgFor?.image_url && (
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/public/medicine-image/${imgFor.id}?v=${imgFor.image_version || ""}`} alt={imgFor?.name} className="w-24 h-24 rounded-xl object-cover border border-slate-200" />
            )}
            <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3 space-y-1">
              <div className="font-bold">Photo criteria (website fast rakhne ke liye):</div>
              <div>• Sirf <b>WebP</b> format</div>
              <div>• <b>Square</b> size — 600×600 px best (300–1200 px allowed)</div>
              <div>• Max <b>500 KB</b> file size</div>
            </div>
            <Button onClick={() => imgFileRef.current?.click()} disabled={imgUploading} data-testid="med-image-choose-btn" className="w-full bg-emerald-900 hover:bg-emerald-800">
              {imgUploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Camera className="w-4 h-4 mr-2" />}
              {imgFor?.image_url ? "Replace Photo" : "Choose WebP Photo"}
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImgFor(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, v, on, type = "text", placeholder, testid }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input data-testid={testid} type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} className="mt-1 h-10" placeholder={placeholder} />
    </div>
  );
}
